/**
 * Card - Flexible container with default / glass / elevated variants, optional
 * title, subtitle, and body slots. Mirrors the Game UI Kit Card components.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type CardVariant = 'default' | 'glass' | 'elevated';

export interface CardOptions extends BaseUIComponentOptions {
  /** Heading text shown at the top of the card. */
  title?: string;
  /** Smaller text shown beneath the title. */
  subtitle?: string;
  /** Body copy beneath the header. */
  body?: string;
  /** Visual variant. */
  variant?: CardVariant;
}

export class Card extends BaseUIComponent<CardOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Card',
    category: 'data',
    summary: 'Surface container with default, glass, and elevated variants.',
    useCases: ['panel', 'tile', 'modal body', 'section', 'callout'],
    optionsType: 'CardOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/card.html',
      styles: '@engine/assets/ui/styles/card.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private subtitleElement: HTMLElement | null = null;
  private bodyElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Card.metadata.assetPaths.template,
      stylesPath: Card.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<CardOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      title: '',
      subtitle: '',
      body: '',
      variant: 'default',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      title: this.options.title,
      subtitle: this.options.subtitle,
      body: this.options.body,
      variant: this.options.variant,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-card') as HTMLElement | null;
    this.titleElement = this.layout.querySelector('[data-card-title]') as HTMLElement | null;
    this.subtitleElement = this.layout.querySelector('[data-card-subtitle]') as HTMLElement | null;
    this.bodyElement = this.layout.querySelector('[data-card-body]') as HTMLElement | null;
  }

  /** Update the card heading. */
  public setTitle(title: string): void {
    this.options.title = title;
    if (this.titleElement) this.titleElement.textContent = title;
  }

  /** Update the subtitle beneath the title. */
  public setSubtitle(subtitle: string): void {
    this.options.subtitle = subtitle;
    if (this.subtitleElement) this.subtitleElement.textContent = subtitle;
  }

  /** Update the card body copy. */
  public setBody(body: string): void {
    this.options.body = body;
    if (this.bodyElement) this.bodyElement.textContent = body;
  }

  /** Swap the visual variant. */
  public setVariant(variant: CardVariant): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`variant-${this.options.variant}`);
    this.options.variant = variant;
    this.rootElement.classList.add(`variant-${variant}`);
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.titleElement = null;
    this.subtitleElement = null;
    this.bodyElement = null;
  }
}
